import type { Map as MapLibreMap } from "maplibre-gl";
import type { StreetViewPublicKeys } from "../../shared/street-view-keys.ts";
import { naverStreetViewAvailable } from "../../shared/street-view-keys.ts";
import { openNaverStreetViewTab } from "./open-naver-street-view-tab.ts";
import { getStreetViewKeys } from "./street-view-state.ts";

export interface StreetViewMapRegistration {
  map: MapLibreMap;
  button: HTMLButtonElement;
  getCenter: () => { lng: number; lat: number };
}

export interface StreetViewKeyboardTarget {
  button: HTMLButtonElement;
  open: () => void;
}

const registrations: StreetViewMapRegistration[] = [];
const keyboardTargets: StreetViewKeyboardTarget[] = [];

export function streetViewButtonShouldShow(keys: StreetViewPublicKeys): boolean {
  return naverStreetViewAvailable(keys);
}

function openStreetViewForRegistration(entry: StreetViewMapRegistration, keys: StreetViewPublicKeys): void {
  const naverKey = keys.naver_map_key_id?.trim();
  if (!naverKey) return;
  const center = entry.getCenter();
  openNaverStreetViewTab({
    center,
    maplibreZoom: entry.map.getZoom(),
    naverMapKeyId: naverKey,
  });
}

function bindStreetViewRegistration(entry: StreetViewMapRegistration, keys: StreetViewPublicKeys): void {
  const { button } = entry;
  if (!streetViewButtonShouldShow(keys)) {
    button.hidden = true;
    return;
  }
  button.hidden = false;
  if (button.dataset.streetViewBound === "1") return;
  const open = () => openStreetViewForRegistration(entry, getStreetViewKeys());
  button.addEventListener("click", (e) => {
    e.stopPropagation();
    open();
  });
  registerKeyboardTarget(button, open);
  button.dataset.streetViewBound = "1";
}

export function registerStreetViewMap(entry: StreetViewMapRegistration): void {
  registrations.push(entry);
  bindStreetViewRegistration(entry, getStreetViewKeys());
}

export function listStreetViewKeyboardTargets(): readonly StreetViewKeyboardTarget[] {
  return keyboardTargets;
}

function registerKeyboardTarget(button: HTMLButtonElement, open: () => void): void {
  if (keyboardTargets.some((t) => t.button === button)) return;
  keyboardTargets.push({ button, open });
}

export function applyStreetViewKeysToMaps(keys: StreetViewPublicKeys): void {
  for (const entry of registrations) {
    bindStreetViewRegistration(entry, keys);
  }
}

/** @internal test hook */
export function _bindStreetViewKeyboardForTest(button: HTMLButtonElement, open: () => void): void {
  registerKeyboardTarget(button, open);
}

/** @internal test hook */
export function _resetStreetViewRegistrationsForTest(): void {
  registrations.length = 0;
  keyboardTargets.length = 0;
}
